/* AuthContext — single source of truth for the signed-in user.
 *
 * Storage strategy:
 * - Tokens come back from the backend as both HttpOnly cookies AND a JSON
 *   body. The cookies are what we use on the web. The JSON body is also
 *   stashed in localStorage so we can:
 *     a. Auto-attach an Authorization header (defense against cookies being
 *        blocked by Safari ITP / iOS Capacitor / cross-origin oddities).
 *     b. Refresh the page without losing identity.
 *   When we move to Capacitor we'll swap localStorage for SecureStorage.
 * - On mount we hit /api/auth/me — if cookies still valid we get the user
 *   immediately. If 401 we try /api/auth/refresh; on success we update
 *   tokens + user. On failure we treat the user as logged-out.
 */
import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCESS_KEY = "wv:access";
const REFRESH_KEY = "wv:refresh";
const HAS_ACCOUNT_KEY = "wv:has-account";

/** Returns true if this device has ever successfully authenticated.
 *  Used by public landing pages to redirect returning users to /login
 *  when their session has expired. The marker is intentionally NOT cleared
 *  on logout — once you have an account, you keep getting routed to /login. */
export function hasEverAuthenticated() {
  try { return localStorage.getItem(HAS_ACCOUNT_KEY) === "1"; } catch { return false; }
}

function markAccountExists() {
  try { localStorage.setItem(HAS_ACCOUNT_KEY, "1"); } catch {}
}

const AuthContext = createContext(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth() must be used inside <AuthProvider>");
  return ctx;
}

export function getStoredAccessToken() {
  try { return localStorage.getItem(ACCESS_KEY) || ""; } catch { return ""; }
}

// Persisted "last signed-in email" so components (e.g. AppDashboard) can
// read their localStorage cache keyed by email SYNCHRONOUSLY on first
// mount, BEFORE AuthContext has finished its async /api/auth/me probe.
// Without this the dashboard's instant-load cache lookup misses on every
// fresh reload, making the whole "painted from cache" experience fail.
const LAST_EMAIL_KEY = "wv:last_email";
export function getLastSignedInEmail() {
  try { return localStorage.getItem(LAST_EMAIL_KEY) || ""; } catch { return ""; }
}
function rememberEmail(email) {
  try { if (email) localStorage.setItem(LAST_EMAIL_KEY, email); } catch {}
}
function forgetEmail() {
  try { localStorage.removeItem(LAST_EMAIL_KEY); } catch {}
}

function storeTokens(access, refresh) {
  try {
    if (access) localStorage.setItem(ACCESS_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  } catch {}
}

function clearTokens() {
  try {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  } catch {}
}

// Build axios config that includes both cookies and a Bearer header (the
// backend accepts either). Belt and suspenders for cross-origin safety.
function authConfig() {
  const t = getStoredAccessToken();
  return {
    withCredentials: true,
    headers: t ? { Authorization: `Bearer ${t}` } : {},
  };
}

export function AuthProvider({ children }) {
  // user states: undefined = checking, null = logged-out, object = signed-in
  const [user, setUser] = useState(undefined);

  const login = useCallback(async (email, password) => {
    const r = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    storeTokens(r.data.access_token, r.data.refresh_token);
    markAccountExists();
    rememberEmail(r.data.user?.email);
    setUser(r.data.user);
    return r.data.user;
  }, []);

  const signup = useCallback(async (email, password, firstName) => {
    const r = await axios.post(
      `${API}/auth/signup`,
      { email, password, first_name: firstName },
      { withCredentials: true }
    );
    storeTokens(r.data.access_token, r.data.refresh_token);
    markAccountExists();
    rememberEmail(r.data.user?.email);
    setUser(r.data.user);
    return r.data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, authConfig());
    } catch {}
    clearTokens();
    forgetEmail();
    setUser(null);
  }, []);

  const refreshSelf = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/auth/me`, authConfig());
      markAccountExists();
      rememberEmail(r.data?.email);
      setUser(r.data);
      return r.data;
    } catch (e) {
      // Try refresh once before giving up.
      try {
        const refresh = localStorage.getItem(REFRESH_KEY);
        const cfg = refresh
          ? { withCredentials: true, headers: { Authorization: `Bearer ${refresh}` } }
          : { withCredentials: true };
        const r2 = await axios.post(`${API}/auth/refresh`, {}, cfg);
        storeTokens(r2.data.access_token, r2.data.refresh_token);
        markAccountExists();
        setUser(r2.data.user);
        return r2.data.user;
      } catch {
        clearTokens();
        setUser(null);
        return null;
      }
    }
  }, []);

  // Forgot / reset password helpers (don't change auth state).
  const forgotPassword = useCallback(async (email) => {
    return axios.post(`${API}/auth/forgot`, { email }, { withCredentials: true });
  }, []);

  const resetPassword = useCallback(async (token, newPassword) => {
    return axios.post(`${API}/auth/reset`, { token, new_password: newPassword }, { withCredentials: true });
  }, []);

  useEffect(() => {
    refreshSelf();
  }, [refreshSelf]);

  // Global axios interceptor: on 401, try refresh then retry once.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      async (error) => {
        const orig = error.config || {};
        if (error.response?.status === 401 && !orig._retried && !orig.url?.includes("/auth/")) {
          orig._retried = true;
          try {
            const refresh = localStorage.getItem(REFRESH_KEY);
            const cfg = refresh
              ? { withCredentials: true, headers: { Authorization: `Bearer ${refresh}` } }
              : { withCredentials: true };
            const r2 = await axios.post(`${API}/auth/refresh`, {}, cfg);
            storeTokens(r2.data.access_token, r2.data.refresh_token);
            setUser(r2.data.user);
            // Re-attach Authorization header on the retry.
            orig.headers = { ...(orig.headers || {}), Authorization: `Bearer ${r2.data.access_token}` };
            return axios(orig);
          } catch {
            clearTokens();
            setUser(null);
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  const value = useMemo(
    () => ({
      user,
      isLoading: user === undefined,
      isAuthenticated: !!user,
      login, signup, logout, refreshSelf,
      forgotPassword, resetPassword,
      authConfig,
    }),
    [user, login, signup, logout, refreshSelf, forgotPassword, resetPassword]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Format a FastAPI error detail safely for rendering — handles 422 arrays. */
export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
